import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

import ChannelIcon from "../../../components/ChannelIcon";
import { i18n } from "../../../translate/i18n";
import { useAddableChannels } from "./channelOptions";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 16 },
  header: { display: "flex", alignItems: "flex-start", gap: 14, padding: "22px 24px 8px" },
  badge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
  },
  title: { margin: 0, fontSize: 20, fontWeight: 700, color: theme.palette.text.primary },
  subtitle: { margin: "2px 0 0", fontSize: 13, color: theme.palette.text.secondary },
  close: { marginLeft: "auto", marginTop: -6 },
  grid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    padding: "12px 24px 20px",
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr", padding: "12px 16px 16px" },
  },
  card: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "16px 16px",
    borderRadius: 12,
    border: "1px solid",
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    color: "inherit",
    transition: "box-shadow 0.15s ease, transform 0.15s ease",
    "&:hover": { boxShadow: "0 4px 14px rgba(15, 23, 42, 0.10)", transform: "translateY(-1px)" },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  muted: { opacity: 0.6 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: theme.palette.text.primary },
  cardText: { fontSize: 12.5, lineHeight: 1.45, color: theme.palette.text.secondary, marginTop: 2 },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 24px 16px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * Selector de canal (doc 0447, D7): un recuadro por canal, teñido con su color de marca, en
 * lugar del antiguo menú desplegable. Referencia: «Agregar Conexión» de Whaticket.
 * Las acciones de cada canal son las mismas de siempre (`channelOptions.js`).
 */
const AddConnectionDialog = ({ open, onClose, user, onAdd, onAddWebForm }) => {
  const classes = useStyles();
  const channels = useAddableChannels(user);

  const choose = (opt) => {
    onClose();
    opt.onSelect({ onAdd, onAddWebForm, user });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <span className={classes.badge}>
          <AddIcon />
        </span>
        <div>
          <h2 className={classes.title}>{i18n.t("connections.addDialog.title")}</h2>
          <p className={classes.subtitle}>{i18n.t("connections.addDialog.subtitle")}</p>
        </div>
        <IconButton size="small" className={classes.close} onClick={onClose} aria-label={i18n.t("connections.addDialog.cancel")}>
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.grid}>
        {channels.map((opt) => (
          <button
            type="button"
            key={opt.key}
            className={`${classes.card} ${opt.sinConfigurar ? classes.muted : ""}`}
            style={{
              borderColor: `${opt.tint}40`,
              background: `linear-gradient(135deg, ${opt.tint}1F 0%, ${opt.tint}08 60%, transparent 100%)`,
            }}
            onClick={() => choose(opt)}
          >
            <ChannelIcon provider={opt.provider} size={30} />
            <span>
              <div className={classes.cardTitle}>{i18n.t(`connections.addDialog.channels.${opt.key}.title`)}</div>
              <div className={classes.cardText}>
                {opt.sinConfigurar
                  ? i18n.t("connections.addDialog.notConfigured")
                  : i18n.t(`connections.addDialog.channels.${opt.key}.description`)}
              </div>
            </span>
          </button>
        ))}
      </div>

      <div className={classes.footer}>
        <Button color="primary" onClick={onClose} style={{ textTransform: "none", fontWeight: 600 }}>
          {i18n.t("connections.addDialog.cancel")}
        </Button>
      </div>
    </Dialog>
  );
};

export default AddConnectionDialog;
